import React, { FC } from 'react';

import { useUnit } from 'effector-react/ssr';

import * as Select from '@radix-ui/react-select';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import { ProductsListSortMode } from '@domains/products-catalog';

import { $productsList, setSortMode } from '@stores/products-list';

import { searchSortingSelectItems, sortingSelectItems } from '@utils/products-list/constants';
import { makeProductListQuery } from '@utils/products-list/query';
import { makeCategoryRoute, makeSearchRoute } from '@utils/route';

import { ChevronDownIcon } from '@components/icons';

import styles from './CategorySortingSelect.module.scss';

export type CategorySortingSelectProps = {
    className?: string;
};

export const CategorySortingSelect: FC<CategorySortingSelectProps> = (props) => {
    const { className } = props;

    const { searchQuery, sortMode, categorySlug, filters } = useUnit($productsList);
    const setSortModeFn = useUnit(setSortMode);

    const router = useRouter();

    const isCategory = searchQuery === null && categorySlug !== null;
    const items = isCategory ? sortingSelectItems : searchSortingSelectItems;

    const onValueChange = (value: ProductsListSortMode) => {
        setSortModeFn(value);
        router.push(
            {
                pathname: isCategory ? makeCategoryRoute(categorySlug) : makeSearchRoute(),
                query: makeProductListQuery({
                    searchQuery,
                    sortMode: value,
                    filters,
                }),
            },
            undefined,
            { scroll: false },
        );
    };

    return (
        <Select.Root value={sortMode} onValueChange={onValueChange}>
            <Select.Trigger className={clsx(styles.trigger, className)}>
                <div className={styles.triggerContent}>
                    <Select.Value className={styles.value} />
                </div>
                <Select.Icon className={styles.dropdownIconWrapper}>
                    <ChevronDownIcon className={styles.dropdownIcon} />
                </Select.Icon>
            </Select.Trigger>
            <Select.Portal>
                <Select.Content
                    className={styles.content}
                    ref={(instance) => {
                        if (!instance) return;
                        // FIX PROPAGATION
                        instance.ontouchend = (e) => {
                            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                            e.cancelable && e.preventDefault();
                        };
                    }}
                >
                    <Select.Viewport>
                        {items.map(({ id, label }) => (
                            <Select.Item key={id} value={id} className={styles.item}>
                                <Select.ItemText>{label}</Select.ItemText>
                            </Select.Item>
                        ))}
                    </Select.Viewport>
                </Select.Content>
            </Select.Portal>
        </Select.Root>
    );
};
